import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Contenedor con scrollbar custom SIEMPRE visible y arrastrable.
 * Evita el overlay de macOS / Chrome que oculta la barra nativa.
 */
export function AlwaysVisibleScrollArea({
  children,
  className = "",
  contentClassName = "",
  trackClassName = "",
  thumbClassName = "",
}) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [thumb, setThumb] = useState({ top: 0, height: 40, visible: false });

  const updateThumb = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const canScroll = scrollHeight > clientHeight + 1;
    if (!canScroll) {
      setThumb({ top: 0, height: clientHeight, visible: true });
      return;
    }

    const trackHeight = trackRef.current
      ? trackRef.current.clientHeight
      : clientHeight;
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(32, Math.round(trackHeight * ratio));
    const maxTop = trackHeight - height;
    const top = Math.round((scrollTop / (scrollHeight - clientHeight)) * maxTop);

    setThumb({
      top: Math.max(0, Math.min(maxTop, top)),
      height,
      visible: true,
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    updateThumb();
    el.addEventListener("scroll", updateThumb, { passive: true });

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateThumb);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }

    const mo = new MutationObserver(updateThumb);
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    window.addEventListener("resize", updateThumb);
    const t = window.setTimeout(updateThumb, 100);

    return () => {
      el.removeEventListener("scroll", updateThumb);
      window.removeEventListener("resize", updateThumb);
      window.clearTimeout(t);
      if (ro) ro.disconnect();
      mo.disconnect();
    };
  }, [updateThumb, children]);

  const onThumbMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const el = viewportRef.current;
    const track = trackRef.current;
    if (!el || !track) return;

    dragRef.current = {
      startY: event.clientY,
      startTop: thumb.top,
    };

    const onMove = (moveEvent) => {
      if (!dragRef.current) return;
      const trackHeight = track.clientHeight;
      const maxTop = trackHeight - thumb.height;
      const delta = moveEvent.clientY - dragRef.current.startY;
      const nextTop = Math.max(
        0,
        Math.min(maxTop, dragRef.current.startTop + delta)
      );
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTop = maxTop > 0 ? (nextTop / maxTop) * maxScroll : 0;
    };

    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onTrackMouseDown = (event) => {
    if (event.target !== trackRef.current) return;
    event.preventDefault();
    event.stopPropagation();

    const el = viewportRef.current;
    const track = trackRef.current;
    if (!el || !track) return;

    const bounds = track.getBoundingClientRect();
    const clickY = event.clientY - bounds.top;
    const maxTop = track.clientHeight - thumb.height;
    const nextTop = Math.max(0, Math.min(maxTop, clickY - thumb.height / 2));
    const maxScroll = el.scrollHeight - el.clientHeight;
    el.scrollTop = maxTop > 0 ? (nextTop / maxTop) * maxScroll : 0;
  };

  return (
    <div className={`avs ${className}`.trim()}>
      <div
        ref={viewportRef}
        className={`avs__viewport ${contentClassName}`.trim()}
      >
        {children}
      </div>
      <div
        ref={trackRef}
        className={`avs__track ${trackClassName}`.trim()}
        onMouseDown={onTrackMouseDown}
      >
        <div
          className={`avs__thumb ${thumbClassName}`.trim()}
          style={{
            transform: `translateY(${thumb.top}px)`,
            height: `${thumb.height}px`,
            opacity: thumb.visible ? 1 : 0.5,
          }}
          onMouseDown={onThumbMouseDown}
        />
      </div>
    </div>
  );
}
